import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import router from './router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './config/queryClientFactory';
import CookieBot from 'react-cookiebot';
import { SENTRY_DSN, SENTRY_ENVIRONMENT } from './constants/config.ts';
import { startVersionCheck } from './utils/versionCheck';

const domainGroupId = '67696235-e0b5-419e-9788-b37934d81c9e';

const getEventMessage = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): string => {
  if (hint.originalException instanceof Error) {
    return hint.originalException.message;
  }

  if (typeof hint.originalException === 'string') {
    return hint.originalException;
  }

  return event.exception?.values?.[0]?.value ?? '';
};

const shouldDropThirdPartyError = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
) => {
  const message = getEventMessage(event, hint);
  const lastFrameFilename =
    event.exception?.values?.[0]?.stacktrace?.frames?.at(-1)?.filename ?? '';

  if (message.includes('__firefox__.refresh_youtube_quality')) {
    return true;
  }

  if (
    /Missing required parameters:.*sitekey/i.test(message) &&
    lastFrameFilename.includes('gstatic.com/recaptcha')
  ) {
    return true;
  }

  return false;
};

if (SENTRY_DSN && import.meta.env.PROD) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        networkDetailAllowUrls: [
          window.location.origin,
          /^https:\/\/shopping\.yettel\.rs/
        ],
        networkRequestHeaders: ['Cache-Control', 'X-Version'],
        networkResponseHeaders: ['Referrer-Policy']
      }),
      Sentry.httpClientIntegration()
    ],
    beforeSend(event, hint) {
      if (shouldDropThirdPartyError(event, hint)) {
        return null;
      }

      return event;
    },
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/^https:\/\/shopping\.yettel\.rs/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
    {import.meta.env.PROD && <CookieBot domainGroupId={domainGroupId} />}
  </React.StrictMode>
);

if (import.meta.env.PROD) {
  // Defer until the browser is idle — keeps the manifest fetch out of the
  // first-paint critical path. setTimeout fallback covers iOS Safari < 16.4.
  const schedule = (cb: () => void) =>
    'requestIdleCallback' in window
      ? window.requestIdleCallback(cb, { timeout: 2000 })
      : setTimeout(cb, 0);
  schedule(startVersionCheck);
}
